import { useEffect } from 'react';

import { isOperationLoading, hasOperationLoaded, PageSkeleton } from '@gw/ui';
import { Navigate, Outlet, useLocation } from 'react-router-dom';

import { AppLayout } from '@/components/layouts/AppLayout';
import { useAuthStore, useOrganizationTokensStore, useStore } from '@/stores/store';

/**
 * ProtectedRoute - Guards routes that require authentication AND organization selection
 * Redirects to login page if user is not authenticated
 * Redirects to org selection if authenticated but no organization selected
 * Exception: global_admin users can bypass org selection (GW-462)
 *
 * @see GW-443 - Wait for checkAuth to complete before trusting isAuthenticated
 * @see GW-645 - Redirect users with pending invitations to org selection
 */
export function ProtectedRoute() {
  const location = useLocation();
  const {
    isAuthenticated,
    isLoading: authLoading,
    hasLoaded: authHasLoaded,
    user,
  } = useAuthStore();
  const {
    currentOrg,
    userOrganizations,
    isLoading: orgLoading,
    hasLoaded: orgHasLoaded,
    fetchUserOrganizations,
    // GW-645: Check for pending invitations to redirect to org selection
    pendingUserInvitations,
    fetchPendingUserInvitations,
  } = useStore();
  const {
    fetchOrganizationBalance,
    hasLoaded: tokensHasLoaded,
    isLoading: tokensLoading,
    errors: tokensErrors,
  } = useOrganizationTokensStore();

  // Check if auth check is in progress
  const isCheckingAuth = isOperationLoading(authLoading, 'checkAuth');
  // GW-443: Check if auth check has completed (not just "not loading")
  const hasCheckedAuth = hasOperationLoaded(authHasLoaded, 'checkAuth');
  const isFetchingOrgs = isOperationLoading(orgLoading, 'fetch');
  const hasLoadedOrgs = hasOperationLoaded(orgHasLoaded, 'fetch');
  // GW-645: Check pending invitations loading state
  const isFetchingPendingInvitations = isOperationLoading(
    orgLoading,
    'fetchPendingUserInvitations'
  );
  const hasLoadedPendingInvitations = hasOperationLoaded(
    orgHasLoaded,
    'fetchPendingUserInvitations'
  );
  // GW-1708: Check token balance loading state
  const isFetchingBalance = isOperationLoading(tokensLoading, 'fetchOrganizationBalance');
  const hasLoadedBalance = hasOperationLoaded(tokensHasLoaded, 'fetchOrganizationBalance');
  const balanceFetchFailed = !!tokensErrors?.fetchOrganizationBalance;

  // Fetch organizations if authenticated but orgs not loaded yet
  useEffect(() => {
    if (isAuthenticated && !hasLoadedOrgs && !isFetchingOrgs) {
      fetchUserOrganizations();
    }
  }, [isAuthenticated, hasLoadedOrgs, isFetchingOrgs, fetchUserOrganizations]);

  // GW-645: Fetch pending invitations if authenticated but not yet loaded
  useEffect(() => {
    if (isAuthenticated && !hasLoadedPendingInvitations && !isFetchingPendingInvitations) {
      fetchPendingUserInvitations();
    }
  }, [
    isAuthenticated,
    hasLoadedPendingInvitations,
    isFetchingPendingInvitations,
    fetchPendingUserInvitations,
  ]);

  // GW-1708: Fetch organization token balance when org is available
  // Guard against infinite retry on failure (e.g. CORS error in local dev)
  useEffect(() => {
    if (
      isAuthenticated &&
      currentOrg?.id &&
      !hasLoadedBalance &&
      !isFetchingBalance &&
      !balanceFetchFailed
    ) {
      fetchOrganizationBalance(currentOrg.id).catch(() => {
        // error captured in tokensErrors; effect will not retry
      });
    }
  }, [
    isAuthenticated,
    currentOrg?.id,
    hasLoadedBalance,
    isFetchingBalance,
    balanceFetchFailed,
    fetchOrganizationBalance,
  ]);

  // GW-443: Show loading until checkAuth has COMPLETED verification
  // GW-798: However, if we have persisted auth state (user + isAuthenticated + currentOrg),
  // trust it immediately while checkAuth runs in background. This fixes the race condition
  // where hasLoaded is not persisted, causing a brief 404 flash on page reload.
  // If persisted auth is stale, checkAuth() (called in App.tsx on mount) will update state.
  const hasPersistedAuth = isAuthenticated && user && currentOrg;

  if (isCheckingAuth || (!hasCheckedAuth && !hasPersistedAuth)) {
    return (
      <AppLayout variant="bare">
        <div className="flex min-h-full items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <div className="border-primary h-8 w-8 animate-spin rounded-full border-4 border-t-transparent" />
            <p className="text-muted-foreground text-sm">Checking authentication...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    const returnTo = location.pathname + location.search;
    // Don't append redirect param if already on /auth/* routes to avoid self-referential redirects
    const shouldAppendRedirect = !location.pathname.startsWith('/auth');
    const loginPath = shouldAppendRedirect
      ? `/auth/login?redirect=${encodeURIComponent(returnTo)}`
      : '/auth/login';
    return <Navigate to={loginPath} replace />;
  }

  // Show loading while fetching organizations
  if (isFetchingOrgs || (!hasLoadedOrgs && !currentOrg)) {
    return (
      <AppLayout variant="default">
        <PageSkeleton />
      </AppLayout>
    );
  }

  // Check if user is global_admin (GW-462: global admins can bypass org selection)
  const isGlobalAdmin = user?.role === 'global_admin';

  // GW-645: Redirect to org selection if user has multiple orgs OR pending invitations
  // This ensures new users invited to an org get to choose/accept the invitation
  // Exception: global_admin users can access protected routes without org selection
  const hasPendingInvitations = pendingUserInvitations.length > 0;
  const needsOrgSelection = userOrganizations.length > 1 || hasPendingInvitations;

  if (hasLoadedOrgs && !currentOrg && needsOrgSelection && !isGlobalAdmin) {
    return <Navigate to="/auth/select-organization" replace />;
  }

  // When orgs are loaded but none found and no invitations pending, show an account configuration
  // error. The API now returns 500 for data-inconsistent users (GW-6231), so this state means
  // the user has corrupted membership data.
  if (
    hasLoadedOrgs &&
    !currentOrg &&
    userOrganizations.length === 0 &&
    !hasPendingInvitations &&
    !isGlobalAdmin
  ) {
    return (
      <AppLayout variant="bare">
        <div className="flex min-h-full items-center justify-center">
          <div className="text-center">
            <h2 className="text-destructive text-lg font-semibold">Account Configuration Error</h2>
            <p className="text-muted-foreground mt-2 text-sm">
              Your account has no organization. Please contact support to restore your access.
            </p>
          </div>
        </div>
      </AppLayout>
    );
  }

  // GW-6534: Block render in the post-signIn / pre-fetchUserOrganizations
  // window. After authSlice.signIn clears `currentOrg`, children of the
  // protected layout must NOT mount with `currentOrg === null` — that lets
  // them issue API calls with no org context (or, with the BaseApiClient
  // localStorage fallback we just removed, with the previous user's org).
  // The branches above handle multi-org and zero-org cases; this final guard
  // catches the single-org window where `selectOrganization` has not yet
  // fired. organizationSlice.fetchUserOrganizations auto-selects the single
  // org, so this guard only flashes briefly between fetch completion and
  // auto-selection — but flashing "Loading organization" is correct: it
  // prevents children from issuing API calls with stale org context.
  if (isAuthenticated && !currentOrg && !needsOrgSelection && !isGlobalAdmin) {
    return (
      <AppLayout variant="default">
        <PageSkeleton />
      </AppLayout>
    );
  }

  return <Outlet />;
}
